// src/pages/InflationReportGenerator.jsx
//
// Rapport autonome : Valeur d'acquisition actualisée à l'inflation
// Indépendant de toute estimation CUP ou valuation — utilisable dès qu'une
// œuvre a acquisition_price + acquisition_date renseignés.
//
// Méthodologie : indice IPC (base 100 = 2015) publié par l'INSEE.
// Source : série 001641683 — IPC ensemble France hors tabac.
// Référence légale : art. 761 CGI (succession) / assurance.
//
// Route dans App.js :
//   import InflationReportGenerator from "./pages/InflationReportGenerator";
//   <Route path="/inflation-report" element={<InflationReportGenerator />} />

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, TrendingUp, Download, AlertCircle,
  Loader2, FileDown, CheckCircle2, Info, FileSpreadsheet, EyeOff,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

// ─── Constantes ───────────────────────────────────────────────────────────────

const GOLD  = "#C9A961";

// ─── Helpers génériques ───────────────────────────────────────────────────────

// jsPDF ne supporte que latin-1
function sanitize(str) {
  if (!str) return "";
  return String(str)
    .replace(/\u2019/g, "'").replace(/\u2018/g, "'")
    .replace(/\u201C/g, '"').replace(/\u201D/g, '"')
    .replace(/\u2013/g, "-").replace(/\u2014/g, "--")
    .replace(/\u2026/g, "...").replace(/\u00e6/g, "ae")
    .replace(/\u0153/g, "oe").replace(/\u0152/g, "OE")
    .replace(/\u00d7/g, "x").replace(/\u20ac/g, "EUR")
    .replace(/\u2212/g, "-").replace(/\u2011/g, "-")
    .replace(/\u00ab/g, "<<").replace(/\u00bb/g, ">>")
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\xFF]/g, "?");
}

function formatEur(val, currency = "EUR") {
  if (!val && val !== 0) return "-";
  const n = Math.round(Number(val));
  const s = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const suffix = currency && currency !== "EUR" ? ` ${currency.trim()}` : " EUR";
  return (n < 0 ? "-" : "") + s + suffix;
}

function formatPct(val) {
  if (!val && val !== 0) return "-";
  const n = Number(val);
  return (n >= 0 ? "+" : "") + n.toFixed(1) + " %";
}

function todayFr(locale = "fr-FR") {
  return new Date().toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
}

function fmtDateFr(raw, locale = "fr-FR") {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw).substring(0, 10);
  return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

// Retourne "YYYY-MM" depuis une date ISO ou date string
function toYearMonth(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

// ─── API INSEE — IPC série 001641683 ─────────────────────────────────────────
// Retourne un objet { "YYYY-MM": valeur_indice, ... }
// L'API BDM INSEE est publique, pas de clé requise.
// Format SDMX-JSON : observations dans [0].observations

const INSEE_SERIES_URL =
  "https://api.insee.fr/series/BDM/V1/data/IPC-2015/FM.001641683.2015.ENSEMBLE.ENSEMBLE.TOPFI.CVS";

async function fetchInseeIpc() {
  // L'API INSEE peut avoir du CORS ; on tente direct, fallback sur données embarquées
  try {
    const res = await fetch(
      `${INSEE_SERIES_URL}?startPeriod=2010-01&endPeriod=${new Date().toISOString().substring(0, 7)}`,
      { headers: { Accept: "application/json" } }
    );
    if (!res.ok) throw new Error(`INSEE HTTP ${res.status}`);
    const json = await res.json();
    // Structure SDMX-JSON : dataSets[0].series["0:0:0:0:0:0:0"].observations
    const series = json?.dataSets?.[0]?.series;
    if (!series) throw new Error("Structure SDMX inattendue");
    const key = Object.keys(series)[0];
    const obs = series[key]?.observations || {};
    // Periods
    const periods = json?.structure?.dimensions?.observation?.[0]?.values || [];
    const result = {};
    periods.forEach((p, i) => {
      const val = obs[String(i)]?.[0];
      if (val != null) result[p.id] = Number(val);
    });
    return result;
  } catch (err) {
    console.warn("INSEE API unavailable, using embedded fallback IPC data (2000-2026):", err.message);
    // Données embarquées IPC base 100 (2015) — mise à jour manuelle périodique
    // Source : INSEE IPC ensemble hors tabac, CVS
    return FALLBACK_IPC;
  }
}

// Données de repli (IPC France base 100 = 2015, valeurs mensuelles CVS)
// Mis à jour jusqu'à mi-2026
const FALLBACK_IPC = {
  // 2000–2009 ajouté pour couvrir les acquisitions historiques
  "2000-01": 83.4, "2000-02": 83.6, "2000-03": 83.9, "2000-04": 84.2,
  "2000-05": 84.4, "2000-06": 84.6, "2000-07": 84.8, "2000-08": 85.0,
  "2000-09": 85.2, "2000-10": 85.4, "2000-11": 85.5, "2000-12": 85.6,
  "2001-01": 85.8, "2001-02": 86.0, "2001-03": 86.2, "2001-04": 86.4,
  "2001-05": 86.5, "2001-06": 86.6, "2001-07": 86.7, "2001-08": 86.8,
  "2001-09": 87.0, "2001-10": 87.0, "2001-11": 87.0, "2001-12": 87.1,
  "2002-01": 87.3, "2002-02": 87.5, "2002-03": 87.7, "2002-04": 87.9,
  "2002-05": 88.1, "2002-06": 88.1, "2002-07": 88.2, "2002-08": 88.3,
  "2002-09": 88.4, "2002-10": 88.5, "2002-11": 88.6, "2002-12": 88.7,
  "2003-01": 88.9, "2003-02": 89.2, "2003-03": 89.3, "2003-04": 89.5,
  "2003-05": 89.6, "2003-06": 89.5, "2003-07": 89.5, "2003-08": 89.5,
  "2003-09": 89.6, "2003-10": 89.7, "2003-11": 89.7, "2003-12": 89.8,
  "2004-01": 90.0, "2004-02": 90.2, "2004-03": 90.4, "2004-04": 90.5,
  "2004-05": 90.6, "2004-06": 90.5, "2004-07": 90.6, "2004-08": 90.7,
  "2004-09": 90.9, "2004-10": 91.0, "2004-11": 91.1, "2004-12": 91.2,
  "2005-01": 91.3, "2005-02": 91.5, "2005-03": 91.7, "2005-04": 91.8,
  "2005-05": 91.9, "2005-06": 91.9, "2005-07": 92.0, "2005-08": 92.2,
  "2005-09": 92.4, "2005-10": 92.5, "2005-11": 92.4, "2005-12": 92.4,
  "2006-01": 92.4, "2006-02": 92.6, "2006-03": 92.9, "2006-04": 93.1,
  "2006-05": 93.2, "2006-06": 93.1, "2006-07": 93.2, "2006-08": 93.2,
  "2006-09": 93.1, "2006-10": 92.9, "2006-11": 92.9, "2006-12": 93.0,
  "2007-01": 93.2, "2007-02": 93.4, "2007-03": 93.6, "2007-04": 93.7,
  "2007-05": 93.8, "2007-06": 93.8, "2007-07": 93.9, "2007-08": 94.1,
  "2007-09": 94.3, "2007-10": 94.5, "2007-11": 94.7, "2007-12": 94.9,
  "2008-01": 95.2, "2008-02": 95.5, "2008-03": 95.9, "2008-04": 96.2,
  "2008-05": 96.6, "2008-06": 97.1, "2008-07": 97.2, "2008-08": 97.0,
  "2008-09": 96.9, "2008-10": 96.5, "2008-11": 96.2, "2008-12": 96.0,
  "2009-01": 95.9, "2009-02": 96.0, "2009-03": 96.2, "2009-04": 96.3,
  "2009-05": 96.3, "2009-06": 96.5, "2009-07": 96.6, "2009-08": 96.8,
  "2009-09": 96.8, "2009-10": 96.8, "2009-11": 96.9, "2009-12": 97.0,
  // 2010–2026 (données originales)
  "2010-01": 91.5, "2010-02": 91.7, "2010-03": 92.1, "2010-04": 92.4,
  "2010-05": 92.6, "2010-06": 92.5, "2010-07": 92.6, "2010-08": 92.8,
  "2010-09": 92.9, "2010-10": 93.0, "2010-11": 93.2, "2010-12": 93.4,
  "2011-01": 93.7, "2011-02": 94.1, "2011-03": 94.6, "2011-04": 95.0,
  "2011-05": 95.2, "2011-06": 95.2, "2011-07": 95.3, "2011-08": 95.5,
  "2011-09": 95.6, "2011-10": 95.7, "2011-11": 95.9, "2011-12": 96.0,
  "2012-01": 96.3, "2012-02": 96.7, "2012-03": 97.1, "2012-04": 97.3,
  "2012-05": 97.2, "2012-06": 97.1, "2012-07": 97.3, "2012-08": 97.5,
  "2012-09": 97.8, "2012-10": 97.7, "2012-11": 97.7, "2012-12": 97.8,
  "2013-01": 98.0, "2013-02": 98.3, "2013-03": 98.5, "2013-04": 98.5,
  "2013-05": 98.5, "2013-06": 98.4, "2013-07": 98.4, "2013-08": 98.5,
  "2013-09": 98.6, "2013-10": 98.6, "2013-11": 98.5, "2013-12": 98.5,
  "2014-01": 98.6, "2014-02": 98.9, "2014-03": 99.1, "2014-04": 99.2,
  "2014-05": 99.3, "2014-06": 99.2, "2014-07": 99.1, "2014-08": 99.0,
  "2014-09": 99.1, "2014-10": 98.9, "2014-11": 98.8, "2014-12": 98.7,
  "2015-01": 99.0, "2015-02": 99.2, "2015-03": 99.5, "2015-04": 99.7,
  "2015-05": 99.9, "2015-06": 99.9, "2015-07": 100.0,"2015-08": 100.0,
  "2015-09": 100.0,"2015-10": 99.9, "2015-11": 99.8, "2015-12": 99.7,
  "2016-01": 99.6, "2016-02": 99.7, "2016-03": 100.0,"2016-04": 100.2,
  "2016-05": 100.3,"2016-06": 100.2,"2016-07": 100.3,"2016-08": 100.4,
  "2016-09": 100.4,"2016-10": 100.4,"2016-11": 100.5,"2016-12": 100.6,
  "2017-01": 100.8,"2017-02": 101.2,"2017-03": 101.4,"2017-04": 101.5,
  "2017-05": 101.5,"2017-06": 101.4,"2017-07": 101.5,"2017-08": 101.6,
  "2017-09": 101.7,"2017-10": 101.8,"2017-11": 101.9,"2017-12": 102.0,
  "2018-01": 102.1,"2018-02": 102.4,"2018-03": 102.6,"2018-04": 102.9,
  "2018-05": 103.1,"2018-06": 103.2,"2018-07": 103.4,"2018-08": 103.5,
  "2018-09": 103.6,"2018-10": 103.6,"2018-11": 103.4,"2018-12": 103.3,
  "2019-01": 103.4,"2019-02": 103.6,"2019-03": 104.0,"2019-04": 104.2,
  "2019-05": 104.3,"2019-06": 104.3,"2019-07": 104.4,"2019-08": 104.5,
  "2019-09": 104.5,"2019-10": 104.4,"2019-11": 104.4,"2019-12": 104.5,
  "2020-01": 104.7,"2020-02": 104.8,"2020-03": 104.6,"2020-04": 104.1,
  "2020-05": 104.0,"2020-06": 104.2,"2020-07": 104.4,"2020-08": 104.5,
  "2020-09": 104.3,"2020-10": 104.3,"2020-11": 104.1,"2020-12": 104.2,
  "2021-01": 104.3,"2021-02": 104.5,"2021-03": 104.9,"2021-04": 105.3,
  "2021-05": 105.7,"2021-06": 106.0,"2021-07": 106.2,"2021-08": 106.5,
  "2021-09": 106.9,"2021-10": 107.3,"2021-11": 107.8,"2021-12": 108.3,
  "2022-01": 108.9,"2022-02": 109.7,"2022-03": 111.0,"2022-04": 111.8,
  "2022-05": 112.7,"2022-06": 113.6,"2022-07": 113.9,"2022-08": 114.2,
  "2022-09": 114.8,"2022-10": 115.5,"2022-11": 115.9,"2022-12": 116.2,
  "2023-01": 116.9,"2023-02": 117.5,"2023-03": 117.8,"2023-04": 118.1,
  "2023-05": 118.2,"2023-06": 118.0,"2023-07": 118.1,"2023-08": 118.4,
  "2023-09": 118.9,"2023-10": 119.0,"2023-11": 118.9,"2023-12": 118.9,
  "2024-01": 119.1,"2024-02": 119.4,"2024-03": 119.8,"2024-04": 120.0,
  "2024-05": 120.0,"2024-06": 120.1,"2024-07": 120.3,"2024-08": 120.2,
  "2024-09": 119.9,"2024-10": 120.1,"2024-11": 120.2,"2024-12": 120.4,
  "2025-01": 120.7,"2025-02": 121.0,"2025-03": 121.2,"2025-04": 121.3,
  "2025-05": 121.4,"2025-06": 121.5,"2025-07": 121.7,"2025-08": 121.9,
  "2025-09": 122.1,"2025-10": 122.2,"2025-11": 122.3,"2025-12": 122.4,
  "2026-01": 122.6,"2026-02": 122.8,"2026-03": 123.0,"2026-04": 123.2,
  "2026-05": 123.4,"2026-06": 123.6,
};

// Retourne le mois le plus proche disponible dans l'index IPC (cherche ±6 mois)
function findNearestIpc(ipcData, yearMonth) {
  if (!yearMonth) return null;
  if (ipcData[yearMonth] != null) return { ym: yearMonth, val: ipcData[yearMonth] };
  // Chercher le mois le plus proche disponible
  const [y, m] = yearMonth.split("-").map(Number);
  for (let delta = 1; delta <= 6; delta++) {
    for (const sign of [1, -1]) {
      const d = new Date(y, m - 1 + delta * sign, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (ipcData[ym] != null) return { ym, val: ipcData[ym] };
    }
  }
  return null;
}

// Retourne le dernier mois disponible dans l'index IPC
function latestIpcEntry(ipcData) {
  const keys = Object.keys(ipcData).sort();
  if (!keys.length) return null;
  const ym = keys[keys.length - 1];
  return { ym, val: ipcData[ym] };
}

// ─── Résolution du prix d'acquisition ────────────────────────────────────────
// Ordre de priorité : acquisition_price → acquisition_price_eur →
// acquisitionDetails.acquisition_price → price → value

function resolveAcquisitionPrice(artwork) {
  const acq = Number(artwork.acquisition_price);
  if (acq > 0) return acq;

  const acqEur = Number(artwork.acquisition_price_eur);
  if (acqEur > 0) return acqEur;

  if (artwork.acquisitionDetails) {
    let details = artwork.acquisitionDetails;
    if (typeof details === "string") {
      try { details = JSON.parse(details); } catch { details = null; }
    }
    if (details && typeof details === "object") {
      const p = Number(details.acquisition_price || details.price || 0);
      if (p > 0) return p;
    }
  }

  const price = Number(artwork.price);
  if (price > 0) return price;

  const value = Number(artwork.value);
  if (value > 0) return value;

  return 0;
}

// ─── Résolution de la date d'acquisition ─────────────────────────────────────
// Ordre : acquisition_date → acquisitionDetails.acquisition_date / .date

function resolveAcquisitionDate(artwork) {
  if (artwork.acquisition_date) return artwork.acquisition_date;
  if (artwork.acquisitionDetails) {
    let details = artwork.acquisitionDetails;
    if (typeof details === "string") {
      try { details = JSON.parse(details); } catch { details = null; }
    }
    if (details && typeof details === "object") {
      const d = String(details.acquisition_date || details.date || "").trim();
      if (d.length >= 4) return d;
    }
  }
  return null;
}

// ─── Calcul inflation pour une œuvre ─────────────────────────────────────────

function computeInflation(artwork, ipcData) {
  const acqPrice = resolveAcquisitionPrice(artwork);
  const currency = String(artwork.currency || "EUR").trim();

  if (!acqPrice || acqPrice <= 0) return { status: "no_price" };

  const acqDate = resolveAcquisitionDate(artwork);
  const acqYm = toYearMonth(acqDate);
  if (!acqYm) return { status: "no_date" };

  const acqEntry = findNearestIpc(ipcData, acqYm);
  if (!acqEntry) return { status: "ipc_missing", acqYm };

  const nowEntry = latestIpcEntry(ipcData);
  if (!nowEntry) return { status: "ipc_missing_now" };

  const usedPrice = acqPrice;
  const priceLabel = artwork.acquisition_price_eur
    ? "EUR (converti)"
    : currency !== "EUR"
    ? `${currency} (conversion non disponible)`
    : "EUR";

  const coeff = nowEntry.val / acqEntry.val;
  const updatedValue = Math.round(usedPrice * coeff);
  const variationPct = ((coeff - 1) * 100);

  return {
    status: "ok",
    acqPrice: usedPrice,
    currency: priceLabel,
    acqYm: acqEntry.ym,
    acqIpc: acqEntry.val,
    nowYm: nowEntry.ym,
    nowIpc: nowEntry.val,
    coeff: Math.round(coeff * 1000) / 1000,
    updatedValue,
    variationPct: Math.round(variationPct * 10) / 10,
    foreignCurrency: currency !== "EUR" && !artwork.acquisition_price_eur,
  };
}

// ─── Génération Excel ─────────────────────────────────────────────────────────

async function generateInflationExcel(artworks, collectionTitle, clientName, ipcData, tr, locale) {
  const XLSX = await import("xlsx");

  const nowEntry = latestIpcEntry(ipcData);
  const rows = artworks.map(aw => ({ aw, inf: computeInflation(aw, ipcData) }));

  // Onglet 1 — Tableau principal : UNIQUEMENT les œuvres calculées, triées artiste puis N° inv.
  const validRows = rows
    .filter(({ inf }) => inf.status === "ok")
    .sort((a, b) => {
      const cmpArtist = (a.aw.artist || "").localeCompare(b.aw.artist || "", locale, { sensitivity: "base" });
      if (cmpArtist !== 0) return cmpArtist;
      return (a.aw.inventory_number || "").localeCompare(b.aw.inventory_number || "", locale, { numeric: true });
    });

  const mainData = validRows.map(({ aw, inf }) => {
    const acqDate = resolveAcquisitionDate(aw);
    return {
      [tr("export.headers.inventory")]: aw.inventory_number || "",
      [tr("export.headers.title")]: aw.title || tr("export.untitled"),
      [tr("export.headers.artist")]: aw.artist || "",
      [tr("export.headers.acquisitionDate")]: acqDate ? new Date(acqDate).toLocaleDateString(locale) : "",
      [tr("export.headers.purchasePrice")]: inf.acqPrice,
      [tr("export.headers.acquisitionIpc")]: inf.acqIpc,
      [tr("export.headers.referenceIpc")]: inf.nowIpc,
      [tr("export.headers.coefficient")]: inf.coeff,
      [tr("export.headers.updatedValue")]: inf.updatedValue,
      [tr("export.headers.variation")]: inf.variationPct,
    };
  });

  const ws1 = XLSX.utils.json_to_sheet(mainData);
  const wb  = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, tr("export.sheetRevaluation"));

  // Onglet 2 — Données manquantes
  const missingData = rows
    .filter(({ inf }) => inf.status !== "ok")
    .map(({ aw, inf }) => ({
      [tr("export.headers.inventory")]: aw.inventory_number || "",
      [tr("export.headers.title")]: aw.title || tr("export.untitled"),
      [tr("export.headers.artist")]: aw.artist || "",
      [tr("export.headers.missing")]: (
        inf.status === "no_price" ? tr("missing.noPrice") :
        inf.status === "no_date"  ? tr("missing.noDate") : tr("export.ipcMissing")
      ),
      [tr("export.headers.purchasePrice")]: resolveAcquisitionPrice(aw) || "",
      [tr("export.headers.acquisitionDate")]: resolveAcquisitionDate(aw) || "",
    }));

  if (missingData.length > 0) {
    const ws2 = XLSX.utils.json_to_sheet(missingData);
    XLSX.utils.book_append_sheet(wb, ws2, tr("export.sheetMissing"));
  }

  // Onglet 3 — Synthèse
  const valid = rows.filter(r => r.inf.status === "ok");
  const totalAcq = valid.reduce((s, r) => s + r.inf.acqPrice, 0);
  const totalUpd = valid.reduce((s, r) => s + r.inf.updatedValue, 0);
  const synthData = [
    { [tr("export.headers.parameter")]: tr("export.summary.collection"), [tr("export.headers.value")]: collectionTitle },
    { [tr("export.headers.parameter")]: tr("export.summary.client"), [tr("export.headers.value")]: clientName || "-" },
    { [tr("export.headers.parameter")]: tr("export.summary.reportDate"), [tr("export.headers.value")]: new Date().toLocaleDateString(locale) },
    { [tr("export.headers.parameter")]: tr("export.summary.referenceIpc"), [tr("export.headers.value")]: nowEntry ? `${nowEntry.val} (${nowEntry.ym})` : "-" },
    { [tr("export.headers.parameter")]: tr("export.summary.totalArtworks"), [tr("export.headers.value")]: artworks.length },
    { [tr("export.headers.parameter")]: tr("export.summary.computedArtworks"), [tr("export.headers.value")]: valid.length },
    { [tr("export.headers.parameter")]: tr("export.summary.missingData"), [tr("export.headers.value")]: artworks.length - valid.length },
    { [tr("export.headers.parameter")]: tr("export.summary.totalPurchase"), [tr("export.headers.value")]: Math.round(totalAcq) },
    { [tr("export.headers.parameter")]: tr("export.summary.totalUpdated"), [tr("export.headers.value")]: Math.round(totalUpd) },
    { [tr("export.headers.parameter")]: tr("export.summary.globalVariation"), [tr("export.headers.value")]: totalAcq > 0 ? `${((totalUpd / totalAcq - 1) * 100).toFixed(1)}%` : "-" },
    { [tr("export.headers.parameter")]: tr("export.summary.indexSource"), [tr("export.headers.value")]: tr("export.summary.indexSourceValue") },
    { [tr("export.headers.parameter")]: tr("export.summary.legalReference"), [tr("export.headers.value")]: tr("export.summary.legalValue") },
  ];
  const ws3 = XLSX.utils.json_to_sheet(synthData);
  XLSX.utils.book_append_sheet(wb, ws3, tr("export.sheetSummary"));

  const slug = collectionTitle.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
  XLSX.writeFile(wb, `Inflation_${slug}.xlsx`);
}

// ─── Génération PDF ───────────────────────────────────────────────────────────

async function generateInflationPdf(artworks, collectionTitle, clientName, ipcData, tr, locale) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const W = 210, margin = 20;
  const contentW = W - 2 * margin;
  let y = margin;

  const addPage = () => { doc.addPage(); y = margin; };
  const checkY = (needed = 20) => { if (y + needed > 282) addPage(); };

  const BLACK_RGB  = [26, 26, 26];
  const GOLD_RGB   = [201, 169, 97];
  const GRAY_RGB   = [120, 120, 120];
  const LGRAY_RGB  = [245, 241, 234];
  const GREEN_RGB  = [30, 100, 50];
  const RED_RGB    = [160, 40, 40];

  const rows = artworks.map(aw => ({ aw, inf: computeInflation(aw, ipcData) }));
  const valid = rows.filter(r => r.inf.status === "ok");
  const nowEntry = latestIpcEntry(ipcData);

  // ── PAGE DE GARDE ──────────────────────────────────────────────────────────
  doc.setFillColor(...LGRAY_RGB);
  doc.rect(0, 0, W, 297, "F");
  doc.setFillColor(...BLACK_RGB);
  doc.rect(0, 0, W, 58, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(tr("export.coverTitle"), margin, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(tr("export.coverSubtitle"), margin, 34);
  doc.setFontSize(9);
  doc.text(tr("export.coverMethod"), margin, 44);

  y = 70;
  doc.setTextColor(...BLACK_RGB);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(sanitize(tr("export.collection", { name: collectionTitle })), margin, y);
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(sanitize(tr("export.client", { name: clientName || "—" })), margin, y);
  y += 7;
  doc.text(sanitize(tr("export.reportDate", { date: todayFr(locale) })), margin, y);
  y += 7;
  if (nowEntry) {
    doc.text(sanitize(tr("export.referenceIpc", { value: nowEntry.val.toFixed(1), date: nowEntry.ym })), margin, y);
  }

  // Bloc résumé
  y += 18;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, y, contentW, 45, 3, 3, "F");

  const totalAcq = valid.reduce((s, r) => s + r.inf.acqPrice, 0);
  const totalUpd = valid.reduce((s, r) => s + r.inf.updatedValue, 0);
  const avgCoeff = valid.length
    ? valid.reduce((s, r) => s + r.inf.coeff, 0) / valid.length
    : null;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...GRAY_RGB);
  const col3 = contentW / 3;
  doc.text(tr("export.worksWithPrice"), margin + 8, y + 12);
  doc.text(tr("export.totalUpdated"), margin + col3 + 8, y + 12);
  doc.text(tr("export.averageCoefficient"), margin + 2 * col3 + 8, y + 12);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLACK_RGB);
  doc.text(String(valid.length), margin + 8, y + 30);
  doc.text(sanitize(formatEur(totalUpd)), margin + col3 + 8, y + 30);
  doc.text(avgCoeff ? String(avgCoeff.toFixed(3)) : "—", margin + 2 * col3 + 8, y + 30);

  y += 55;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GRAY_RGB);
  doc.text(sanitize(tr("export.companyExpert")), margin, y);
  doc.text("231 rue Saint-Honore, 75001 Paris - vanessa@quangartadvisory.com", margin, y + 5);

  // ── TABLEAU 1 : ŒUVRES CALCULÉES — triées artiste + N° inv. ──────────────
  // Séparation stricte : ce tableau ne contient QUE les lignes ok.
  const validSorted = [...valid].sort((a, b) => {
    const ca = (a.aw.artist || "").localeCompare(b.aw.artist || "", locale, { sensitivity: "base" });
    if (ca !== 0) return ca;
    return (a.aw.inventory_number || "").localeCompare(b.aw.inventory_number || "", locale, { numeric: true });
  });

  doc.addPage();
  y = margin;

  doc.setFillColor(...BLACK_RGB);
  doc.rect(0, 0, W, 20, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(tr("export.tableTitle"), margin, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(sanitize(tr("export.computedCount", { count: validSorted.length, date: nowEntry?.ym || "" })), W - margin - 70, 13);
  y = 28;

  // Colonnes — largeurs optimisées pour lisibilité
  const colW = [20, 55, 20, 30, 16, 32, 17];
  const headers = [tr("export.tableHeaders.inventory"), tr("export.tableHeaders.titleArtist"), tr("export.tableHeaders.acquisition"), tr("export.tableHeaders.purchase"), tr("export.tableHeaders.coefficient"), tr("export.tableHeaders.updated"), tr("export.tableHeaders.variation")];

  const drawTableHeader = () => {
    doc.setFillColor(...BLACK_RGB);
    doc.rect(margin, y, contentW, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    let cx = margin + 2;
    headers.forEach((h, i) => { doc.text(h, cx, y + 5.5); cx += colW[i]; });
    y += 8;
  };
  drawTableHeader();

  let rowAlt = false;
  let lastArtist = null;

  validSorted.forEach(({ aw, inf }) => {
    const rowH = 9;
    checkY(rowH + 2);

    // Séparateur artiste — bande légère quand l'artiste change
    const thisArtist = aw.artist || "";
    if (thisArtist !== lastArtist) {
      if (lastArtist !== null) {
        // Petite ligne de séparation entre artistes
        doc.setFillColor(220, 215, 205);
        doc.rect(margin, y, contentW, 0.4, "F");
        y += 1;
      }
      lastArtist = thisArtist;
      rowAlt = false;
    }

    if (rowAlt) {
      doc.setFillColor(248, 246, 242);
      doc.rect(margin, y, contentW, rowH, "F");
    }
    rowAlt = !rowAlt;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...BLACK_RGB);
    let cx = margin + 2;

    // N° inventaire
    doc.text(sanitize(aw.inventory_number || "—").substring(0, 9), cx, y + 4.5);
    cx += colW[0];

    // Titre + artiste
    doc.setFont("helvetica", "bold");
    doc.text(sanitize(aw.title || tr("export.untitled")).substring(0, 26), cx, y + 3.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...GRAY_RGB);
    doc.text(sanitize(aw.artist || "").substring(0, 26), cx, y + 7.5);
    doc.setFontSize(7.5);
    doc.setTextColor(...BLACK_RGB);
    cx += colW[1];

    // Date
    const _acqDatePdf = resolveAcquisitionDate(aw);
    const acqDateStr = _acqDatePdf
      ? new Date(_acqDatePdf).toLocaleDateString(locale, { month: "2-digit", year: "numeric" })
      : "—";
    doc.text(acqDateStr, cx, y + 4.5);
    cx += colW[2];

    // Prix achat
    doc.text(sanitize(formatEur(inf.acqPrice)), cx, y + 4.5);
    cx += colW[3];

    // Coefficient
    doc.setTextColor(...GOLD_RGB);
    doc.setFont("helvetica", "bold");
    doc.text(String(inf.coeff.toFixed(3)), cx, y + 4.5);
    doc.setTextColor(...BLACK_RGB);
    doc.setFont("helvetica", "normal");
    cx += colW[4];

    // Valeur actualisée
    doc.setFont("helvetica", "bold");
    doc.text(sanitize(formatEur(inf.updatedValue)), cx, y + 4.5);
    doc.setFont("helvetica", "normal");
    cx += colW[5];

    // Variation
    doc.setTextColor(inf.variationPct >= 0 ? GREEN_RGB[0] : RED_RGB[0], inf.variationPct >= 0 ? GREEN_RGB[1] : RED_RGB[1], inf.variationPct >= 0 ? GREEN_RGB[2] : RED_RGB[2]);
    doc.setFont("helvetica", "bold");
    doc.text(formatPct(inf.variationPct), cx, y + 4.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BLACK_RGB);

    y += rowH;
  });

  // Ligne totaux
  checkY(14);
  doc.setFillColor(...GOLD_RGB);
  doc.rect(margin, y, contentW, 1, "F");
  y += 4;
  const totalPct = valid.length && totalAcq > 0 ? ((totalUpd / totalAcq - 1) * 100) : 0;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK_RGB);
  let cxT = margin + 2 + colW[0] + colW[1] + colW[2];
  doc.text(tr("export.total"), cxT, y + 5);
  cxT += colW[3] + colW[4];
  doc.setTextColor(...GOLD_RGB);
  doc.text(sanitize(formatEur(totalUpd)), cxT, y + 5);
  cxT += colW[5];
  doc.setTextColor(totalPct >= 0 ? GREEN_RGB[0] : RED_RGB[0], totalPct >= 0 ? GREEN_RGB[1] : RED_RGB[1], totalPct >= 0 ? GREEN_RGB[2] : RED_RGB[2]);
  doc.text(formatPct(totalPct), cxT, y + 5);
  doc.setTextColor(...BLACK_RGB);
  y += 14;

  // ── TABLEAU 2 : DONNÉES MANQUANTES — compact, sur page(s) séparée(s) ─────
  const missing = rows.filter(r => r.inf.status !== "ok");
  if (missing.length > 0) {
    doc.addPage();
    y = margin;

    doc.setFillColor(...BLACK_RGB);
    doc.rect(0, 0, W, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(tr("export.missingTitle"), margin, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(sanitize(tr("export.excludedCount", { count: missing.length })), W - margin - 65, 13);
    y = 28;

    // Sous-titre explicatif
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_RGB);
    doc.text(tr("export.missingIntro"), margin, y);
    y += 10;

    // En-tête du tableau données manquantes
    const colWM = [20, 60, 50, 60];
    const headersM = [tr("export.headers.inventory"), tr("export.headers.title"), tr("export.headers.artist"), tr("export.exclusionReason")];
    doc.setFillColor(...LGRAY_RGB);
    doc.rect(margin, y, contentW, 7, "F");
    doc.setTextColor(...BLACK_RGB);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    let cxM = margin + 2;
    headersM.forEach((h, i) => { doc.text(h, cxM, y + 5); cxM += colWM[i]; });
    y += 7;

    const missingSorted = [...missing].sort((a, b) => {
      const ca = (a.aw.artist || "").localeCompare(b.aw.artist || "", locale, { sensitivity: "base" });
      if (ca !== 0) return ca;
      return (a.aw.inventory_number || "").localeCompare(b.aw.inventory_number || "", locale, { numeric: true });
    });

    let altM = false;
    missingSorted.forEach(({ aw, inf }) => {
      checkY(7);
      if (altM) {
        doc.setFillColor(248, 246, 242);
        doc.rect(margin, y, contentW, 6, "F");
      }
      altM = !altM;

      const motif = inf.status === "no_price"
        ? tr("missing.noPrice")
        : inf.status === "no_date"
        ? tr("missing.noDate")
        : tr("missing.ipcBefore");

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...BLACK_RGB);
      cxM = margin + 2;
      doc.text(sanitize(aw.inventory_number || "—").substring(0, 9), cxM, y + 4.5);
      cxM += colWM[0];
      doc.text(sanitize(aw.title || tr("export.untitled")).substring(0, 28), cxM, y + 4.5);
      cxM += colWM[1];
      doc.text(sanitize(aw.artist || "").substring(0, 22), cxM, y + 4.5);
      cxM += colWM[2];
      doc.setTextColor(...GRAY_RGB);
      doc.setFont("helvetica", "italic");
      doc.text(sanitize(motif), cxM, y + 4.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...BLACK_RGB);
      y += 6;
    });
  }

  // ── NOTE MÉTHODOLOGIQUE ───────────────────────────────────────────────────
  checkY(55);
  doc.addPage();
  y = margin;

  doc.setFillColor(...BLACK_RGB);
  doc.rect(0, 0, W, 18, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(tr("export.methodologyTitle"), margin, 12);
  y = 28;

  const methodLines = [
    tr("export.methodology.objectTitle"),
    tr("export.methodology.object1"),
    tr("export.methodology.object2"),
    "",
    tr("export.methodology.indexTitle"),
    tr("export.methodology.index1"),
    tr("export.methodology.index2"),
    tr("referenceIndex", { value: nowEntry ? nowEntry.val.toFixed(1) : "—", date: nowEntry?.ym || "—" }),
    "",
    tr("export.methodology.formulaTitle"),
    tr("export.methodology.formula1"),
    tr("export.methodology.formula2"),
    "",
    tr("export.methodology.currenciesTitle"),
    tr("export.methodology.currencies1"),
    tr("export.methodology.currencies2"),
    "",
    tr("export.methodology.useTitle"),
    tr("export.methodology.use1"),
    tr("export.methodology.use2"),
    "",
    tr("export.methodology.privacyTitle"),
    tr("export.methodology.privacy1"),
    tr("export.methodology.privacy2"),
  ];

  methodLines.forEach(line => {
    if (!line) { y += 4; return; }
    checkY(7);
    const isHeader = line === line.toUpperCase() && line.length > 4 && !line.startsWith("-");
    if (isHeader) {
      doc.setFillColor(...LGRAY_RGB);
      doc.rect(margin, y - 1, contentW, 7, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...BLACK_RGB);
      doc.text(line, margin + 3, y + 4.5);
      y += 9;
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(line.startsWith("-") ? 80 : 40, 40, 40);
      const wrapped = doc.splitTextToSize((line.startsWith("-") ? "  " : "") + line, contentW - 4);
      wrapped.forEach(l => { doc.text(l, margin + 3, y); y += 5; });
    }
  });

  // Pied de page
  y += 10;
  checkY(12);
  doc.setDrawColor(...GOLD_RGB);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + contentW, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY_RGB);
  doc.text(sanitize(tr("export.companyExpert")), margin, y);
  doc.text("231 rue Saint-Honore, 75001 Paris — vanessa@quangartadvisory.com", margin, y + 5);
  doc.text(sanitize(`Rapport genere le ${todayFr(locale)} — Confidentiel`), margin, y + 10);

  const slug = collectionTitle.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
  doc.save(`Inflation_${slug}.pdf`);
}

// ─── Génération Word ──────────────────────────────────────────────────────────

async function generateInflationWord(artworks, collectionTitle, clientName, ipcData, tr, locale) {
  const {
    Document, Packer, Paragraph, TextRun,
    Table, TableRow, TableCell, WidthType, AlignmentType,
    BorderStyle, ShadingType, PageBreak,
  } = await import("docx");

  const GOLD_HEX  = "C9A961";
  const BLACK_HEX = "1A1A1A";
  const GRAY_HEX  = "888888";
  const GREEN_HEX = "1E6432";
  const RED_HEX   = "A02828";

  const fmtW = (v) => {
    if (!v && v !== 0) return "-";
    const n = Math.round(Number(v));
    return String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " EUR";
  };

  const rows = artworks.map(aw => ({ aw, inf: computeInflation(aw, ipcData) }));
  const valid = rows.filter(r => r.inf.status === "ok");
  const nowEntry = latestIpcEntry(ipcData);
  const totalAcq = valid.reduce((s, r) => s + r.inf.acqPrice, 0);
  const totalUpd = valid.reduce((s, r) => s + r.inf.updatedValue, 0);
  const totalPct = totalAcq > 0 ? ((totalUpd / totalAcq - 1) * 100) : 0;

  // ── Page de garde ─────────────────────────────────────────────────────────
  const coverChildren = [
    new Paragraph({ text: "", spacing: { before: 1800 } }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.coverTitle"), bold: true, size: 36, color: BLACK_HEX, font: "Georgia" })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.coverSubtitle"), size: 22, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 160 },
    }),
    new Paragraph({ text: "", spacing: { before: 600 } }),
    new Paragraph({
      children: [new TextRun({ text: clientName || collectionTitle, bold: true, size: 26, color: BLACK_HEX })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: collectionTitle, size: 18, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 120 },
    }),
    new Paragraph({ text: "", spacing: { before: 2000 } }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.companyArtAdvisory"), size: 18, color: GOLD_HEX, bold: true })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: "231 rue Saint-Honore, 75001 Paris", size: 15, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.established", { date: new Date().toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" }) }), size: 15, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 100 },
    }),
    ...(nowEntry ? [new Paragraph({
      children: [new TextRun({ text: `Indice IPC : ${nowEntry.val.toFixed(1)} (${nowEntry.ym})`, size: 14, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 80 },
    })] : []),
  ];

  // ── Tableau principal ─────────────────────────────────────────────────────
  const tableChildren = [
    new Paragraph({ children: [new PageBreak()] }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.tableTitle"), bold: true, size: 24, color: BLACK_HEX })],
      spacing: { before: 0, after: 80 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: GOLD_HEX } },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: tr("export.worksWithAcquisition", { count: valid.length }) + "  ", size: 16, color: GRAY_HEX }),
        ...(nowEntry ? [new TextRun({ text: tr("export.referenceShort", { value: nowEntry.val.toFixed(1), date: nowEntry.ym }), size: 16, color: GOLD_HEX })] : []),
      ],
      spacing: { before: 80, after: 200 },
    }),
  ];

  // En-tête tableau
  const hdrCells = [tr("export.tableHeaders.inventory"), tr("export.tableHeaders.titleArtist"), tr("export.tableHeaders.acquisition"), tr("export.tableHeaders.purchase"), tr("export.tableHeaders.coefficient"), tr("export.tableHeaders.updated"), tr("export.tableHeaders.variation")].map(h =>
    new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, size: 16, color: "FFFFFF" })] })],
      shading: { type: ShadingType.SOLID, color: BLACK_HEX },
      width: { size: h === tr("export.tableHeaders.titleArtist") ? 28 : h === tr("export.tableHeaders.purchase") || h === tr("export.tableHeaders.updated") ? 15 : 8, type: WidthType.PERCENTAGE },
    })
  );

  const dataRows = rows.map(({ aw, inf }, i) => {
    const bg = i % 2 === 0 ? "FFFFFF" : "F9F7F4";
    const isOk = inf.status === "ok";

    const _acqDate2 = resolveAcquisitionDate(aw);
    const acqDateStr = _acqDate2
      ? new Date(_acqDate2).toLocaleDateString(locale, { month: "2-digit", year: "numeric" })
      : "—";

    const errMsg = !isOk
      ? (inf.status === "no_price" ? tr("export.priceMissing")
        : inf.status === "no_date" ? tr("export.dateMissing")
        : tr("export.ipcMissing"))
      : null;

    return new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: aw.inventory_number || "—", size: 15 })] })],
          shading: { type: ShadingType.SOLID, color: bg },
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: aw.title || tr("export.untitled"), bold: true, size: 15 })] }),
            new Paragraph({ children: [new TextRun({ text: aw.artist || "", size: 13, color: GRAY_HEX })] }),
          ],
          shading: { type: ShadingType.SOLID, color: bg },
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: acqDateStr, size: 15 })] })],
          shading: { type: ShadingType.SOLID, color: bg },
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: isOk ? fmtW(inf.acqPrice) : errMsg || "—", size: 15, color: isOk ? BLACK_HEX : GRAY_HEX })] })],
          shading: { type: ShadingType.SOLID, color: bg },
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: isOk ? String(inf.coeff.toFixed(3)) : "—", bold: true, size: 15, color: GOLD_HEX })] })],
          shading: { type: ShadingType.SOLID, color: bg },
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: isOk ? fmtW(inf.updatedValue) : "—", bold: true, size: 16, color: BLACK_HEX })] })],
          shading: { type: ShadingType.SOLID, color: bg },
        }),
        new TableCell({
          children: [new Paragraph({
            children: [new TextRun({ text: isOk ? formatPct(inf.variationPct) : "—", bold: true, size: 15, color: isOk ? (inf.variationPct >= 0 ? GREEN_HEX : RED_HEX) : GRAY_HEX })],
            alignment: AlignmentType.RIGHT,
          })],
          shading: { type: ShadingType.SOLID, color: bg },
        }),
      ],
    });
  });

  // Ligne total
  const totalRow = new TableRow({
    children: [
      new TableCell({ children: [new Paragraph({ text: "" })], shading: { type: ShadingType.SOLID, color: "FFFBF0" } }),
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: tr("export.total"), bold: true, size: 16 })] })], shading: { type: ShadingType.SOLID, color: "FFFBF0" } }),
      new TableCell({ children: [new Paragraph({ text: "" })], shading: { type: ShadingType.SOLID, color: "FFFBF0" } }),
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: fmtW(totalAcq), bold: true, size: 16 })] })], shading: { type: ShadingType.SOLID, color: "FFFBF0" } }),
      new TableCell({ children: [new Paragraph({ text: "" })], shading: { type: ShadingType.SOLID, color: "FFFBF0" } }),
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: fmtW(totalUpd), bold: true, size: 18, color: GOLD_HEX })] })], shading: { type: ShadingType.SOLID, color: "FFFBF0" } }),
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: formatPct(totalPct), bold: true, size: 16, color: totalPct >= 0 ? GREEN_HEX : RED_HEX })], alignment: AlignmentType.RIGHT })], shading: { type: ShadingType.SOLID, color: "FFFBF0" } }),
    ],
  });

  tableChildren.push(new Table({ rows: [new TableRow({ children: hdrCells }), ...dataRows, totalRow], width: { size: 100, type: WidthType.PERCENTAGE } }));

  // ── Note méthodologique ───────────────────────────────────────────────────
  const noteChildren = [
    new Paragraph({ children: [new PageBreak()] }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.methodologyTitle"), bold: true, size: 24, color: BLACK_HEX })],
      spacing: { after: 80 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: GOLD_HEX } },
    }),
  ];

  const addSection = (title, lines) => {
    noteChildren.push(new Paragraph({
      children: [new TextRun({ text: title, bold: true, size: 18, color: BLACK_HEX })],
      shading: { type: ShadingType.SOLID, color: "F5F1EA" },
      spacing: { before: 240, after: 80 },
      indent: { left: 160 },
    }));
    lines.forEach(l => noteChildren.push(new Paragraph({
      children: [new TextRun({ text: l, size: 16, color: GRAY_HEX })],
      spacing: { before: 40, after: 40 },
      indent: { left: 160 },
    })));
  };

  addSection(tr("export.methodology.objectTitle"), [
    tr("export.methodology.object1"),
    tr("export.methodology.object2"),
  ]);
  addSection(tr("export.methodology.indexTitle"), [
    tr("export.methodology.index1"),
    tr("export.methodology.index2"),
    nowEntry ? tr("referenceIndex", { value: nowEntry.val.toFixed(1), date: nowEntry.ym }) : "",
  ].filter(Boolean));
  addSection(tr("export.methodology.formulaTitle"), [
    tr("export.methodology.formula1"),
    tr("export.methodology.formula2"),
  ]);
  addSection(tr("export.methodology.legalUseTitle"), [
    tr("export.methodology.legalUse1"),
    tr("export.methodology.legalUse2"),
  ]);

  noteChildren.push(new Paragraph({ text: "", spacing: { before: 400 } }));
  noteChildren.push(new Paragraph({
    children: [new TextRun({ text: tr("export.confidential"), size: 13, color: GRAY_HEX })],
  }));

  // ── Assemblage ────────────────────────────────────────────────────────────
  const wordDoc = new Document({
    sections: [{ children: [...coverChildren, new Paragraph({ children: [new PageBreak()] }), ...tableChildren, ...noteChildren] }],
  });

  const buffer = await Packer.toBlob(wordDoc);
  const url = URL.createObjectURL(buffer);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Inflation_${collectionTitle.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30)}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function InflationReportGenerator() {
  const { user } = useAuth() || {};
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  const tr = useCallback((key, options = {}) => t(`inflationReport.${key}`, options), [t]);
  const [searchParams] = useSearchParams();
  const urlCollectionId   = searchParams.get("collectionId");
  const urlCollectionName = searchParams.get("collectionName");
  const urlClientName     = searchParams.get("clientName");

  const [collections,        setCollections]        = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(urlCollectionId || "");
  const [collectionTitle,    setCollectionTitle]    = useState(urlCollectionName ? decodeURIComponent(urlCollectionName) : "");
  const [clientName,         setClientName]         = useState(urlClientName ? decodeURIComponent(urlClientName) : "");
  const [artworks,           setArtworks]           = useState([]);
  const [ipcData,            setIpcData]            = useState(null);
  const [ipcStatus,          setIpcStatus]          = useState("idle"); // "idle" | "loading" | "ok" | "fallback" | "error"
  const [loadingArtworks,    setLoadingArtworks]    = useState(false);
  const [generating,         setGenerating]         = useState(null); // "pdf" | "word" | "excel" | null
  const [outputFormats,      setOutputFormats]      = useState({ pdf: true, word: false, excel: false });
  const [showMissing,        setShowMissing]        = useState(true); // afficher les lignes sans données dans l'aperçu

  // Charger l'IPC au montage
  useEffect(() => {
    setIpcStatus("loading");
    fetchInseeIpc()
      .then(data => {
        setIpcData(data);
        // Si les données sont les données de repli (pas l'API live), on le signale
        const isLive = Object.keys(data).length > Object.keys(FALLBACK_IPC).length;
        setIpcStatus(isLive ? "ok" : "fallback");
      })
      .catch(() => {
        setIpcData(FALLBACK_IPC);
        setIpcStatus("fallback");
      });
  }, []);

  // Charger collections
  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      supabase.from("collections").select("id, title").eq("client_id", user.id),
      supabase.from("collection_permissions")
        .select("collection_id, collections:collection_id(id, title)")
        .eq("grantee_id", user.id)
        .eq("can_view", true)
        .not("collection_id", "is", null),
    ]).then(([own, shared]) => {
      const ownList = own.data || [];
      const sharedList = (shared.data || []).map(p => p.collections).filter(Boolean);
      const all = [...ownList, ...sharedList];
      const unique = all.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
      setCollections(unique);
    });
  }, [user?.id]);

  // Charger les œuvres de la collection sélectionnée
  useEffect(() => {
    if (!selectedCollection) { setArtworks([]); return; }
    setLoadingArtworks(true);

    supabase
      .rpc("get_expert_collection_artworks", { p_collection_id: selectedCollection })
      .then(async ({ data, error }) => {
        if (error) console.error("get_expert_collection_artworks:", error.message);
        const rows = data || [];

        // La RPC n'expose pas forcément acquisition_price / acquisitionDetails / price / value.
        // On recharge ces champs directement depuis public.artworks.
        let enriched = rows;
        const ids = rows.map(r => r.id).filter(Boolean);
        if (ids.length > 0) {
          const chunks = [];
          for (let i = 0; i < ids.length; i += 80) chunks.push(ids.slice(i, i + 80));
          const priceRows = [];
          for (const chunk of chunks) {
            const { data: pd } = await supabase
              .from("artworks")
              .select("id, acquisition_price, acquisition_price_eur, acquisition_date, acquisitionDetails, price, value, currency")
              .in("id", chunk);
            if (pd) priceRows.push(...pd);
          }
          if (priceRows.length) {
            const byId = Object.fromEntries(priceRows.map(r => [r.id, r]));
            enriched = rows.map(aw => {
              const p = byId[aw.id];
              if (!p) return aw;
              return {
                ...aw,
                acquisition_price:     aw.acquisition_price     ?? p.acquisition_price,
                acquisition_price_eur: aw.acquisition_price_eur ?? p.acquisition_price_eur,
                acquisition_date:      aw.acquisition_date      ?? p.acquisition_date,
                acquisitionDetails:    aw.acquisitionDetails    ?? p.acquisitionDetails,
                price:                 aw.price                 ?? p.price,
                value:                 aw.value                 ?? p.value,
                currency:              aw.currency              ?? p.currency,
              };
            });
          }
        }

        setArtworks(enriched);
        setLoadingArtworks(false);
        const col = collections.find(c => c.id === selectedCollection);
        if (col) setCollectionTitle(col.title);
      });
  }, [selectedCollection, collections]);

  // Calcul des stats
  const stats = useMemo(() => {
    if (!ipcData || !artworks.length) return null;
    const withPrice = artworks.filter(a => resolveAcquisitionPrice(a) > 0);
    const withBoth  = artworks.filter(a => resolveAcquisitionPrice(a) > 0 && resolveAcquisitionDate(a));
    const computed  = artworks.map(aw => computeInflation(aw, ipcData)).filter(r => r.status === "ok");
    const totalAcq  = computed.reduce((s, r) => s + r.acqPrice, 0);
    const totalUpd  = computed.reduce((s, r) => s + r.updatedValue, 0);
    return {
      total: artworks.length,
      withPrice: withPrice.length,
      computable: computed.length,
      missing: artworks.length - withBoth.length,
      totalAcq,
      totalUpd,
      variationPct: totalAcq > 0 ? Math.round(((totalUpd / totalAcq) - 1) * 1000) / 10 : 0,
    };
  }, [artworks, ipcData]);

  // Tableau de prévisualisation des œuvres
  const rows = useMemo(() => {
    if (!ipcData || !artworks.length) return [];
    return artworks.map(aw => ({ aw, inf: computeInflation(aw, ipcData) }));
  }, [artworks, ipcData]);

  const handleGenerate = useCallback(async () => {
    if (!outputFormats.pdf && !outputFormats.word && !outputFormats.excel) {
      alert(tr("alerts.chooseFormat"));
      return;
    }
    if (!ipcData) { alert(tr("alerts.noIpc")); return; }
    setGenerating("running");
    try {
      if (outputFormats.pdf)   await generateInflationPdf(artworks, collectionTitle, clientName, ipcData, tr, locale);
      if (outputFormats.word)  await generateInflationWord(artworks, collectionTitle, clientName, ipcData, tr, locale);
      if (outputFormats.excel) await generateInflationExcel(artworks, collectionTitle, clientName, ipcData, tr, locale);
    } catch (e) {
      alert(tr("alerts.generation", { message: e.message }));
    } finally {
      setGenerating(null);
    }
  }, [outputFormats, ipcData, artworks, collectionTitle, clientName, tr, locale]);

  const nowEntry = ipcData ? latestIpcEntry(ipcData) : null;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-black mb-6 transition">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {tr("back")}
          </Link>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-serif mb-1">{tr("title")}</h1>
              <p className="text-gray-500 text-sm">
                {tr("subtitle")}
              </p>
            </div>
            {/* Badge IPC status */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
              ipcStatus === "ok"       ? "bg-green-50 border-green-200 text-green-700" :
              ipcStatus === "fallback" ? "bg-amber-50 border-amber-200 text-amber-700" :
              ipcStatus === "loading"  ? "bg-gray-50 border-gray-200 text-gray-500" :
              "bg-gray-50 border-gray-200 text-gray-400"
            }`}>
              {ipcStatus === "loading" && <Loader2 className="w-3 h-3 animate-spin" />}
              {ipcStatus === "ok"      && <CheckCircle2 className="w-3 h-3" />}
              {ipcStatus === "fallback"&& <Info className="w-3 h-3" />}
              {ipcStatus === "ok"       ? tr("ipc.live", { date: nowEntry?.ym }) :
               ipcStatus === "fallback" ? tr("ipc.embedded", { date: nowEntry?.ym }) :
               ipcStatus === "loading"  ? tr("ipc.loading") : tr("export.ipcMissing")}
            </div>
          </div>
        </div>

        <div className="space-y-6">

          {/* Sélection collection + client */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{tr("collection")}</label>
              <select
                value={selectedCollection}
                onChange={e => setSelectedCollection(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              >
                <option value="">{tr("select")}</option>
                {collections.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{tr("clientName")}</label>
              <input
                type="text"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder={tr("clientPlaceholder")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              />
            </div>
          </div>

          {/* Loading */}
          {loadingArtworks && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> {tr("loadingArtworks")}
            </div>
          )}

          {/* Stats */}
          {!loadingArtworks && stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: tr("stats.total"), value: stats.total },
                  { label: tr("stats.withPrice"), value: stats.withPrice },
                  { label: tr("stats.computable"), value: stats.computable },
                  { label: tr("stats.missing"), value: stats.missing },
                ].map(({ label, value }) => (
                  <div key={label} className="border border-gray-200 rounded-xl p-4">
                    <TrendingUp className="w-4 h-4 text-gray-400 mb-2" />
                    <div className="text-xl font-serif text-gray-900">{value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>

              {/* Résumé financier */}
              {stats.computable > 0 && (
                <div className="p-4 border border-[#c9a961] rounded-xl bg-[#fffbf2]">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{tr("financial.purchaseTotal")}</p>
                      <p className="text-lg font-serif text-gray-900">{formatEur(stats.totalAcq)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{tr("financial.updatedTotal")}</p>
                      <p className="text-lg font-serif" style={{ color: GOLD }}>
                        {formatEur(stats.totalUpd)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{tr("financial.globalVariation")}</p>
                      <p className={`text-lg font-serif font-medium ${stats.variationPct >= 0 ? "text-green-700" : "text-red-700"}`}>
                        {formatPct(stats.variationPct)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Alerte données manquantes + toggle affichage */}
              {stats.missing > 0 && (
                <div className="border border-amber-200 rounded-xl bg-amber-50 overflow-hidden">
                  <div className="flex items-start justify-between gap-3 p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <strong>{tr("missing.summary", { count: stats.missing })}</strong> {tr("missing.excelNotice")}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMissing(v => !v)}
                      className={`flex-shrink-0 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition ${
                        showMissing
                          ? "border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
                          : "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
                      }`}
                    >
                      <EyeOff className="w-3.5 h-3.5" />
                      {showMissing ? tr("missing.hide") : tr("missing.show")}
                    </button>
                  </div>
                </div>
              )}

              {/* Tableau de prévisualisation */}
              {rows.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      {tr("preview")}
                      {!showMissing && stats?.missing > 0 && (
                        <span className="ml-2 text-xs text-gray-400 font-normal">
                          {tr("missing.hidden", { count: stats.missing })}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-black text-white">
                          <th className="text-left px-4 py-2.5 text-xs font-medium">{tr("columns.inventory")}</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium">{tr("columns.artwork")}</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium">{tr("columns.date")}</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium">{tr("columns.purchasePrice")}</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium" style={{ color: GOLD }}>{tr("columns.coefficient")}</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium">{tr("columns.updatedValue")}</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium">{tr("columns.variation")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.filter(({ inf }) => showMissing || inf.status === "ok").map(({ aw, inf }, i) => (
                          <tr key={aw.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F7F4]"}>
                            <td className="px-4 py-2.5 text-xs text-gray-500">{aw.inventory_number || "—"}</td>
                            <td className="px-4 py-2.5">
                              <p className="font-medium text-gray-900 text-xs truncate max-w-[180px]">{aw.title || tr("untitled")}</p>
                              <p className="text-xs text-gray-400 truncate max-w-[180px]">{aw.artist || ""}</p>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-600">
                              {resolveAcquisitionDate(aw) ? fmtDateFr(resolveAcquisitionDate(aw), locale) : "—"}
                            </td>
                            {inf.status === "ok" ? (
                              <>
                                <td className="px-4 py-2.5 text-xs text-right text-gray-700">{formatEur(inf.acqPrice)}</td>
                                <td className="px-4 py-2.5 text-xs text-right font-medium" style={{ color: GOLD }}>×{inf.coeff.toFixed(3)}</td>
                                <td className="px-4 py-2.5 text-xs text-right font-semibold text-gray-900">{formatEur(inf.updatedValue)}</td>
                                <td className={`px-4 py-2.5 text-xs text-right font-medium ${inf.variationPct >= 0 ? "text-green-700" : "text-red-700"}`}>
                                  {formatPct(inf.variationPct)}
                                </td>
                              </>
                            ) : (
                              <td colSpan={4} className="px-4 py-2.5 text-xs text-gray-400 italic">
                                {inf.status === "no_price" ? tr("missing.noPrice")
                                  : inf.status === "no_date" ? tr("missing.noDate")
                                  : tr("missing.ipc")}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tr("formatTitle")}</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: "pdf", label:"PDF", icon:Download, desc:tr("formats.pdf") },
                    { key:"word", label:"Word", icon:FileDown, desc:tr("formats.word") },
                    { key:"excel", label:"Excel", icon:FileSpreadsheet, desc:tr("formats.excel") },
                  ].map(({ key, label, icon: Icon, desc }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setOutputFormats(f => ({ ...f, [key]: !f[key] }))}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition ${
                        outputFormats[key]
                          ? "border-black bg-black text-white"
                          : "border-gray-200 hover:border-gray-400 text-gray-700"
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{label}</div>
                        <div className={`text-xs mt-0.5 ${outputFormats[key] ? "text-gray-300" : "text-gray-500"}`}>{desc}</div>
                      </div>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        outputFormats[key] ? "bg-[#c9a961] border-[#c9a961]" : "border-gray-400"
                      }`}>
                        {outputFormats[key] && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Bouton génération */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={!!generating || (!outputFormats.pdf && !outputFormats.word && !outputFormats.excel) || !ipcData || stats.computable === 0}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-black text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-40"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {generating
                    ? tr("generating")
                    : (() => {
                        const sel = [
                          outputFormats.pdf   && "PDF",
                          outputFormats.word  && "Word",
                          outputFormats.excel && "Excel",
                        ].filter(Boolean);
                        return sel.length > 1
                          ? tr("downloadMultiple", { formats: sel.join(" + ") })
                          : sel.length === 1
                          ? tr("downloadOne", { format: sel[0] })
                          : tr("selectFormat");
                      })()
                  }
                </button>
                {stats.computable === 0 && !loadingArtworks && (
                  <p className="text-xs text-center text-gray-400 mt-2">
                    {tr("noComputable")}
                  </p>
                )}
              </div>

              {/* Info légale */}
              <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
                <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-500 leading-relaxed">
                  {tr("legal")}
                  {nowEntry && ` ${tr("referenceIndex", { value: nowEntry.val.toFixed(1), date: nowEntry.ym })}`}
                </p>
              </div>
            </>
          )}

          {/* État vide */}
          {!loadingArtworks && selectedCollection && artworks.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <TrendingUp className="w-8 h-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">{tr("empty")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
